import React, { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, PackagePlus, Loader2, AlertCircle, Split } from 'lucide-react';
import { Deal, DealService } from '../../lib/types';
import { supabase } from '../../lib/supabase';
import {
  createOrderFromDeal,
  loadDealOrderLines,
  messaggioEsito,
  summaryFromDealLines,
  type DealLinkedOrder,
  type DealOrderLinesData,
} from '../../lib/services/dealOrderService';
import { useApp } from '../../components/AppProvider';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { MANUAL_ORDER_STATUS_CONFIG } from '../../lib/types/orders';
import type { ManualOrderStatus } from '../../lib/types/orders';
import { formatCurrency } from '../../lib/utils/formatters';
import DealOrderSplitModal from './DealOrderSplitModal';
import { DealFulfillmentBadge } from './DealFulfillmentBadge';

/**
 * Il ponte fra un'offerta e il magazzino.
 *
 * Compare SOLO se l'offerta contiene righe prodotto: chi vende servizi non deve
 * accorgersi che questa sezione esiste.
 *
 * In testa lo stato di evasione (stesso badge della lista offerte) e, riga per
 * riga, quanto e' in offerta, quanto e' gia' negli ordini non annullati (bozze
 * comprese, indicate a parte) e quanto resta da evadere, in quantita' e valore.
 * Quando gli ordini hanno DDT in uscita spediti, anche quanto e' spedito e
 * consegnato (e ancora da consegnare) per riga: colonne assenti altrimenti.
 *
 * Mostra gli ordini gia' generati dall'offerta. «Trasforma in ordine» (o
 * «Dividi in ordini» quando ce ne sono gia') apre la tabella in cui si sceglie
 * in quanti ordini dividere l'offerta e quanto di ogni articolo va in ciascuno;
 * gli ordini ancora in bozza si possono ridividere.
 *
 * Residui e scrittura passano da flusia-core (`/api/orders/deal-lines`,
 * `/api/orders/deal-plan`), la stessa implementazione usata dal workflow
 * automatico (`/api/orders/from-deal`): una sola regola "da offerta a ordine".
 */

interface DealOrderSectionProps {
  deal: Deal;
  dealServices: DealService[];
}

export const DealOrderSection: React.FC<DealOrderSectionProps> = memo(({ deal, dealServices }) => {
  const navigate = useNavigate();
  const { toast } = useApp();
  const { hasPermission, hasResourceAccess } = usePermissions();

  const [orders, setOrders] = useState<DealLinkedOrder[]>([]);
  /** Righe prodotto con quantita' ancora da ordinare; null = non calcolabile (core non raggiungibile) */
  const [remainingLines, setRemainingLines] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [draftOrders, setDraftOrders] = useState(0);
  /** Si puo' ancora avere un ordine in piu' (limite: uno per unita' da distribuire) */
  const [canAddOrder, setCanAddOrder] = useState(false);
  /** Righe con ordinato/residuo; null = non calcolabile (core non raggiungibile) */
  const [lineData, setLineData] = useState<DealOrderLinesData | null>(null);

  const productLines = dealServices.filter((s) => s.line_type === 'product');
  const hasProductLines = productLines.length > 0;
  const organizationId = (deal as any).organization_id as string;

  const caricaOrdini = useCallback(async (silent = false) => {
    if (!deal?.id) return;
    if (!silent) setLoading(true);
    try {
      const data = await loadDealOrderLines(deal.id, organizationId);
      setLineData(data);
      setOrders(data.orders);
      setRemainingLines(data.lines.filter((l) => l.remaining > 0).length);
      setDraftOrders(data.orders.filter((o) => o.status === 'draft').length);
      setCanAddOrder(data.orders.filter((o) => o.status !== 'cancelled').length < data.maxOrders);
    } catch (err) {
      // flusia-core non raggiungibile o non aggiornato: almeno gli ordini collegati
      console.warn('Righe da ordinare non disponibili, mostro solo gli ordini:', err);
      const { data, error } = await supabase
        .from('orders')
        .select('id, order_number, status, total, order_date')
        .eq('deal_id', deal.id)
        .order('created_at', { ascending: true });
      if (error) console.error('Errore caricamento ordini collegati:', error);
      setOrders((data as DealLinkedOrder[] | null) ?? []);
      setRemainingLines(null);
      setLineData(null);
    } finally {
      setLoading(false);
    }
  }, [deal?.id, organizationId]);

  useEffect(() => {
    if (hasProductLines) caricaOrdini();
    // Righe dell'offerta cambiate (aggiunte, quantita'): residui da ricalcolare
  }, [hasProductLines, caricaOrdini, dealServices]);

  // Offerta accettata e ancora nessun ordine: lo sta creando il server
  // (workflow «Deal won → crea ordine»), arriva qualche secondo dopo.
  // Si rilegge in silenzio qualche volta invece di aspettare la riapertura.
  const [recheck, setRecheck] = useState(0);
  useEffect(() => {
    if (!hasProductLines || deal?.status !== 'accepted' || loading || orders.length > 0 || recheck >= 3) return;
    const t = setTimeout(() => { setRecheck((n) => n + 1); void caricaOrdini(true); }, [3000, 5000, 8000][recheck]);
    return () => clearTimeout(t);
  }, [hasProductLines, deal?.status, loading, orders.length, recheck, caricaOrdini]);

  // Solo se flusia-core non risponde ai residui: creazione diretta come prima
  const handleCreaOrdine = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const outcome = await createOrderFromDeal(deal.id, organizationId);

      if (outcome.skipped) {
        toast.error('Nessuna riga prodotto valida in questa offerta');
        return;
      }

      toast.success(messaggioEsito(outcome));
      await caricaOrdini();
    } catch (err: any) {
      console.error('Errore creazione ordine da offerta:', err);
      toast.error(err?.message || 'Errore durante la creazione dell\'ordine');
    } finally {
      setCreating(false);
    }
  };

  // Offerta di soli servizi: questa sezione non esiste.
  if (!hasProductLines) return null;

  // Senza addon magazzino non esiste nemmeno il posto dove finirebbe l'ordine:
  // la sezione sparisce del tutto, non si mostra disabilitata.
  // `hasResourceAccess` copre entrambi i casi in cui non ha senso mostrarla —
  // modulo non incluso nel piano (globallyDisabledResources, alimentato da
  // subscription_plan.enabled_modules) e ruolo senza alcun accesso al magazzino.
  if (!hasResourceAccess('inventory')) return null;

  const offertaAccettata = deal.status === 'accepted';
  // `inventory` e non `orders`: la risorsa `orders` e' etichettata nella UI dei
  // ruoli ma non ha righe in `permissions` (verificato 2026-09-10), quindi
  // sarebbe negata a chiunque non sia superadmin. Tutto il magazzino, ordini
  // compresi, si regge su `inventory` — la rotta /magazzino chiede inventory.view.
  const puoCreare = hasPermission('inventory', 'create');

  const haOrdini = orders.length > 0;
  const riepilogo = lineData ? summaryFromDealLines(deal.id, lineData) : null;
  const ordiniInBozza = new Set(orders.filter((o) => o.status === 'draft').map((o) => o.id));
  const fmtQta = (q: number) => q.toLocaleString('it-IT', { maximumFractionDigits: 3 });
  // Colonne Spedito/Consegnato solo se almeno una riga e' partita con un DDT
  const conDdt = !!lineData?.lines.some((l) => (l.shipped ?? 0) > 0);
  const valoreDi = (pick: (l: DealOrderLinesData['lines'][number]) => number) =>
    (lineData?.lines ?? []).reduce((sum, l) => sum + l.unit_price * Math.min(pick(l), l.quantity), 0);
  // Senza il calcolo dei residui: si puo' creare solo se non c'e' ancora nessun ordine
  const restaDaOrdinare = remainingLines === null ? !haOrdini : remainingLines > 0;
  // Si puo' dividere finche' resta qualcosa da ordinare, oppure c'e' una bozza e
  // il numero di ordini non ha raggiunto le unita' da distribuire
  const divisibile = remainingLines !== null && (remainingLines > 0 || (draftOrders > 0 && canAddOrder));

  return (
    <div className="py-4 md:py-5 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
      <div className="flex items-center justify-between mb-3 md:mb-4 px-4 md:px-6">
        <h2 className="text-sm font-medium text-[#37352F] dark:text-white flex items-center">
          {orders.length > 1 ? 'Ordini di magazzino' : 'Ordine di magazzino'}
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#787774] dark:text-gray-300 rounded-full">
            {productLines.length} {productLines.length === 1 ? 'prodotto' : 'prodotti'}
          </span>
          {offertaAccettata && riepilogo && (
            <span className="ml-2 inline-flex"><DealFulfillmentBadge summary={riepilogo} /></span>
          )}
        </h2>
      </div>

      <div className="px-4 md:px-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#787774] dark:text-gray-400 py-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Verifica ordini collegati...
          </div>
        ) : (
          <div className="space-y-3">
            {lineData && lineData.lines.length > 0 && (offertaAccettata || haOrdini) && riepilogo && (
              <div className="rounded-md border border-[rgba(55,53,47,0.16)] dark:border-gray-700 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[#787774] dark:text-gray-400 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                      <th className="text-left font-medium px-3 py-2">Articolo</th>
                      <th className="text-right font-medium px-3 py-2 whitespace-nowrap">In offerta</th>
                      <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Ordinato</th>
                      <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Da evadere</th>
                      {conDdt && (
                        <>
                          <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Spedito</th>
                          <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Consegnato</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {lineData.lines.map((line) => {
                      const um = line.unit_of_measure || 'pz';
                      const ordinato = Math.min(line.ordered, line.quantity);
                      const inBozza = Object.entries(line.by_order)
                        .filter(([orderId]) => ordiniInBozza.has(orderId))
                        .reduce((sum, [, q]) => sum + q, 0);
                      return (
                        <tr key={line.deal_service_id} className="border-b last:border-b-0 border-[rgba(55,53,47,0.06)] dark:border-gray-700/60 align-top">
                          <td className="px-3 py-2 text-[#37352F] dark:text-white">
                            <div className="truncate max-w-[220px]" title={line.product_name}>{line.product_name}</div>
                            {line.product_sku && (
                              <div className="text-[10px] text-[#9B9A97] dark:text-gray-500">{line.product_sku}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-[#37352F] dark:text-white">
                            {fmtQta(line.quantity)} {um}
                            <div className="text-[10px] text-[#787774] dark:text-gray-400">{formatCurrency(line.unit_price * line.quantity)}</div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-[#37352F] dark:text-white">
                            {fmtQta(line.ordered)} {um}
                            <div className="text-[10px] text-[#787774] dark:text-gray-400">{formatCurrency(line.unit_price * ordinato)}</div>
                            {inBozza > 0 && (
                              <div className="text-[10px] text-[#9B9A97] dark:text-gray-500">di cui {fmtQta(inBozza)} {um} in bozza</div>
                            )}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${line.remaining > 0 ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-[#787774] dark:text-gray-400'}`}>
                            {fmtQta(line.remaining)} {um}
                            <div className="text-[10px] font-normal">{formatCurrency(line.unit_price * line.remaining)}</div>
                          </td>
                          {conDdt && (() => {
                            const spedito = line.shipped ?? 0;
                            const consegnato = line.delivered ?? 0;
                            const daConsegnare = Math.max(0, line.ordered - consegnato);
                            return (
                              <>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-[#37352F] dark:text-white">
                                  {fmtQta(spedito)} {um}
                                  <div className="text-[10px] text-[#787774] dark:text-gray-400">{formatCurrency(line.unit_price * Math.min(spedito, line.quantity))}</div>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-[#37352F] dark:text-white">
                                  {fmtQta(consegnato)} {um}
                                  <div className="text-[10px] text-[#787774] dark:text-gray-400">{formatCurrency(line.unit_price * Math.min(consegnato, line.quantity))}</div>
                                  {daConsegnare > 0.0005 && (
                                    <div className="text-[10px] text-amber-700 dark:text-amber-300">da consegnare {fmtQta(daConsegnare)} {um}</div>
                                  )}
                                </td>
                              </>
                            );
                          })()}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 font-medium text-[#37352F] dark:text-white">
                      <td className="px-3 py-2">Totale imponibile</td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(riepilogo.total_value ?? 0)}</td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(riepilogo.ordered_value ?? 0)}</td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {formatCurrency(Math.max(0, (riepilogo.total_value ?? 0) - (riepilogo.ordered_value ?? 0)))}
                      </td>
                      {conDdt && (
                        <>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(valoreDi((l) => l.shipped ?? 0))}</td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(valoreDi((l) => l.delivered ?? 0))}</td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {orders.map((order) => {
              const statusConfig =
                MANUAL_ORDER_STATUS_CONFIG[order.status as ManualOrderStatus] ?? {
                  label: order.status,
                  color: 'text-gray-700',
                  bgColor: 'bg-gray-100',
                };
              return (
                <button
                  key={order.id}
                  onClick={() => navigate(`/magazzino?section=orders&order=${order.id}`)}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-md border border-[rgba(55,53,47,0.16)] dark:border-gray-700 hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-gray-800 transition-colors text-left"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#37352F] dark:text-white truncate">
                        {order.order_number}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}
                      >
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="text-xs text-[#787774] dark:text-gray-400 mt-0.5">
                      {order.total != null ? formatCurrency(order.total) : '—'}
                      {order.order_date
                        ? ` · ${new Date(order.order_date).toLocaleDateString('it-IT')}`
                        : ''}
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-[#787774] dark:text-gray-400 shrink-0" />
                </button>
              );
            })}

            {(restaDaOrdinare || divisibile) && (
              <>
                <p className="text-sm text-[#787774] dark:text-gray-400">
                  {!offertaAccettata
                    ? (haOrdini
                        ? "Altre righe potranno diventare ordini quando l'offerta è accettata."
                        : "L'ordine si genera quando l'offerta è accettata.")
                    : !haOrdini
                      ? 'Le righe prodotto di questa offerta possono diventare uno o più ordini di magazzino. I servizi non vengono inclusi.'
                      : remainingLines
                        ? `${remainingLines} ${remainingLines === 1 ? 'riga prodotto ancora da ordinare' : 'righe prodotto ancora da ordinare'}.`
                        : divisibile
                          ? 'Tutte le righe sono negli ordini. Finché sono in bozza puoi dividerle diversamente.'
                          : 'Tutte le righe sono negli ordini.'}
                </p>

                {offertaAccettata && puoCreare && (
                  <div className="flex flex-wrap gap-2">
                    {divisibile ? (
                      <button
                        onClick={() => setShowSplitModal(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-[#0F7B6C] text-white rounded hover:bg-[#0a6257] transition-colors"
                      >
                        {haOrdini ? <Split className="h-3.5 w-3.5" /> : <PackagePlus className="h-3.5 w-3.5" />}
                        {haOrdini ? 'Dividi in ordini' : 'Trasforma in ordine'}
                      </button>
                    ) : (
                      <button
                        onClick={handleCreaOrdine}
                        disabled={creating}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-[#0F7B6C] text-white rounded hover:bg-[#0a6257] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      >
                        {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PackagePlus className="h-3.5 w-3.5" />}
                        {creating ? 'Creazione in corso...' : 'Trasforma in ordine'}
                      </button>
                    )}
                  </div>
                )}

                {offertaAccettata && !puoCreare && (
                  <div className="flex items-start gap-1.5 text-xs text-[#787774] dark:text-gray-400">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    Non hai il permesso di creare ordini.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {showSplitModal && (
        <DealOrderSplitModal
          dealId={deal.id}
          organizationId={organizationId}
          onClose={() => setShowSplitModal(false)}
          onDone={() => {
            setShowSplitModal(false);
            caricaOrdini();
          }}
        />
      )}
    </div>
  );
});

DealOrderSection.displayName = 'DealOrderSection';

export default DealOrderSection;
